import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { motion, AnimatePresence } from 'framer-motion';
import { School, GraduationCap, BookOpen, ChevronRight, ChevronLeft, Check, ChevronDown, Plus, X, Clock, MapPin, AlertCircle } from 'lucide-react';
import ClassForm from './ClassForm';
import { toast } from 'react-hot-toast';

// Mock data - Replace with actual API call
const COLLEGES = [
  "Harvard University",
  "Stanford University",
  "MIT",
  "Yale University",
  "Princeton University",
  // ... add more
];

const MAJORS = [
  // STEM
  { category: "STEM", majors: [
    "Aerospace Engineering",
    "Biology",
    "Biomedical Engineering",
    "Chemical Engineering",
    "Chemistry",
    "Civil Engineering",
    "Computer Engineering",
    "Computer Science",
    "Data Science",
    "Electrical Engineering",
    "Environmental Science",
    "Industrial Engineering",
    "Information Technology",
    "Mathematics",
    "Mechanical Engineering",
    "Physics",
    "Software Engineering",
    "Statistics"
  ]},
  
  // Business
  { category: "Business", majors: [
    "Accounting",
    "Business Administration",
    "Economics",
    "Entrepreneurship",
    "Finance",
    "International Business",
    "Management",
    "Marketing",
    "Supply Chain Management"
  ]},
  
  // Arts & Humanities
  { category: "Arts & Humanities", majors: [
    "Art History",
    "Creative Writing",
    "English",
    "Film Studies",
    "Graphic Design",
    "History",
    "Journalism",
    "Languages",
    "Music",
    "Philosophy",
    "Theater"
  ]},
  
  // Social Sciences
  { category: "Social Sciences", majors: [
    "Anthropology",
    "Communications",
    "Criminal Justice",
    "Education",
    "International Relations",
    "Political Science",
    "Psychology",
    "Public Policy",
    "Sociology"
  ]},
  
  // Health Sciences
  { category: "Health Sciences", majors: [
    "Dental Hygiene",
    "Exercise Science",
    "Healthcare Administration",
    "Nursing",
    "Nutrition",
    "Pharmacy",
    "Physical Therapy",
    "Public Health"
  ]}
];

const SEMESTERS = [
  "Fall 2024",
  "Spring 2025",
  "Summer 2025",
  "Fall 2025",
];

const Dropdown = ({ options, value, onChange, placeholder, fetchOptions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const dropdownRef = useRef(null);
  const debounceTimeout = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = async (term) => {
    setSearchTerm(term);
    
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (term.length < 2) {
      setSuggestions([]);
      return;
    }

    if (fetchOptions) {
      debounceTimeout.current = setTimeout(async () => {
        setLoading(true);
        try {
          const results = await fetchOptions(term);
          setSuggestions(results);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    } else {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(term.toLowerCase())
      );
      setSuggestions(filtered);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="w-full p-3 border border-gray-200 rounded-lg flex justify-between items-center cursor-pointer hover:border-gray-300"
        onClick={() => setIsOpen(!isOpen)}
      >
        <input
          type="text"
          value={searchTerm || value}
          onChange={(e) => {
            handleSearch(e.target.value);
            setIsOpen(true);
          }}
          placeholder={placeholder}
          className="outline-none w-full"
          onClick={(e) => e.stopPropagation()}
        />
        {loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-600" />
        ) : (
          <ChevronDown className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </div>

      {isOpen && (suggestions.length > 0 || loading) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {loading ? (
            <div className="p-3 text-center text-gray-500">Loading...</div>
          ) : (
            suggestions.map((option, index) => (
              <div
                key={index}
                className="p-3 hover:bg-[#F0EDE7] cursor-pointer flex items-center justify-between"
                onClick={() => {
                  onChange(option);
                  setSearchTerm('');
                  setSuggestions([]);
                  setIsOpen(false);
                }}
              >
                {option}
                {value === option && <Check size={16} />}
              </div>
            ))
          )}
        </motion.div>
      )}
    </div>
  );
};

// Helper function to calculate current semester based on date
const calculateCurrentSemester = () => {
  const date = new Date();
  const month = date.getMonth() + 1; // getMonth() returns 0-11
  const year = date.getFullYear();
  
  // Spring: January-May (1-5)
  // Summer: June-July (6-7)
  // Fall: August-December (8-12)
  if (month >= 1 && month <= 5) {
    return `Spring ${year}`;
  } else if (month >= 6 && month <= 7) {
    return `Summer ${year}`;
  } else {
    return `Fall ${year}`;
  }
};

// Helper function to calculate semester start date
const calculateSemesterStart = () => {
  const date = new Date();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  
  // Default start dates for each semester
  if (month >= 1 && month <= 5) {
    return `${year}-01-15`; // Spring semester typically starts mid-January
  } else if (month >= 6 && month <= 7) {
    return `${year}-06-01`; // Summer semester typically starts beginning of June
  } else {
    return `${year}-08-15`; // Fall semester typically starts mid-August
  }
};

// Add this helper function
const semesterToNumber = (semesterString) => {
  const [season, year] = semesterString.split(' ');
  const yearNum = parseInt(year);
  let semesterNum;
  
  switch (season.toLowerCase()) {
    case 'spring':
      semesterNum = 1;
      break;
    case 'summer':
      semesterNum = 2;
      break;
    case 'fall':
      semesterNum = 3;
      break;
    default:
      semesterNum = 1;
  }
  
  // Calculate total semesters since base year (e.g., 2024)
  return ((yearNum - 2024) * 3) + semesterNum;
};

// Update the saveUserProfile function
const saveUserProfile = async (userId, formData) => {
  try {
    const session = await window.Clerk.session;
    if (!session) {
      throw new Error('No active session');
    }

    const token = await session.getToken();
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Clerk-Session-Id': session.id
    };

    // First save the profile
    const profileData = {
      userId,
      school: formData.school,
      major: formData.major,
      current_semester: semesterToNumber(formData.semester),
      college: formData.school
    };

    let profileResult;
    try {
      const profileResponse = await fetch(`https://classify-alpha-a1e5331e0be0.herokuapp.com/api/users/profile`, {
        method: 'POST',
        headers,
        body: JSON.stringify(profileData)
      });

      if (!profileResponse.ok) {
        throw new Error('Failed to save profile');
      }
      
      profileResult = await profileResponse.json();
    } catch (error) {
      console.error('Error saving profile:', error);
      throw new Error('Failed to save profile');
    }

    // Then save each class using the existing endpoint
    if (formData.classes && formData.classes.length > 0) {
      for (const classData of formData.classes) {
        // Convert schedule to JSON format if it's a string
        const scheduleData = classData.schedule ? {
          displayString: classData.schedule,
          // You might want to add more structured data here if needed
          raw: classData.schedule
        } : null;

          await fetch(`https://classify-alpha-a1e5331e0be0.herokuapp.com/api/save-class`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            title: classData.title,
            professor: classData.professor,
            location: classData.location,
            credits: classData.credits,
            schedule: scheduleData // Send as JSON object
          })
        });
      }
    }

    return profileResult;
  } catch (error) {
    console.error('Error saving profile:', error);
    throw error;
  }
};

const OnboardingStep = ({ children, icon: Icon, title, subtitle, stepNumber, totalSteps }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="max-w-xl mx-auto"
  >
    <div className="text-center mb-8">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-[#F0EDE7] flex items-center justify-center">
          <Icon size={32} className="text-gray-700" />
        </div>
      </div>
      <h2 className="text-3xl font-bold text-gray-900 mb-2">{title}</h2>
      <p className="text-gray-600">{subtitle}</p>
    </div>

    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
      {children}
    </div>
  </motion.div>
);

const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="fixed top-0 left-0 right-0 h-1 bg-gray-100">
    <motion.div
      className="h-full bg-[#F0EDE7]"
      initial={{ width: 0 }}
      animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
      transition={{ duration: 0.3 }}
    />
  </div>
);

const NavigationButtons = ({ onBack, onNext, isLastStep, isValid }) => (
  <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
    <div className="max-w-xl mx-auto flex justify-between">
      {onBack ? (
        <button
          onClick={onBack}
          className="flex items-center px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ChevronLeft size={20} className="mr-1" />
          Back
        </button>
      ) : <div />}
      <button
        onClick={onNext}
        disabled={!isValid}
        className={`flex items-center px-8 py-2 rounded-lg transition-colors ${
          isValid
            ? 'bg-[#F0EDE7] hover:bg-[#E5E2DC] text-gray-800'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        {isLastStep ? 'Complete Setup' : 'Continue'}
        <ChevronRight size={20} className="ml-1" />
      </button>
    </div>
  </div>
);

const fetchColleges = async (searchTerm) => {
    const apiKey = 'KyuBdR79R71pnke463UEtmkWXSL0MwzMCXfkVqeH'; // Get free key from https://collegescorecard.ed.gov/data/documentation/
  const url = `https://api.data.gov/ed/collegescorecard/v1/schools?api_key=${apiKey}&school.name=${searchTerm}&fields=school.name,school.city,school.state`;
  
  try {
    const response = await fetch(url);
    const data = await response.json();
    return data.results.map(school => school['school.name']);
  } catch (error) {
    console.error('Error fetching colleges:', error);
    return [];
  }
};

const MajorDropdown = ({ value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredMajors = MAJORS.map(category => ({
    category: category.category,
    majors: category.majors.filter(major =>
      major.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.majors.length > 0);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="w-full p-3 border border-gray-200 rounded-lg flex justify-between items-center cursor-pointer hover:border-gray-300"
        onClick={() => setIsOpen(!isOpen)}
      >
        <input
          type="text"
          value={searchTerm || value}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          placeholder={placeholder}
          className="outline-none w-full"
          onClick={(e) => e.stopPropagation()}
        />
        <ChevronDown 
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[400px] overflow-auto"
        >
          {filteredMajors.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              {/* Only show category if we're not searching or if there are matching majors */}
              {(!searchTerm || category.majors.length > 0) && (
                <div className="px-3 py-2 bg-gray-50 text-sm font-medium text-gray-500">
                  {category.category}
                </div>
              )}
              {category.majors.map((major, majorIndex) => (
                <div
                  key={majorIndex}
                  className="px-3 py-2 hover:bg-[#F0EDE7] cursor-pointer flex items-center justify-between"
                  onClick={() => {
                    onChange(major);
                    setSearchTerm('');
                    setIsOpen(false);
                  }}
                >
                  <span className="text-gray-700">{major}</span>
                  {value === major && <Check size={16} className="text-gray-600" />}
                </div>
              ))}
            </div>
          ))}
          {filteredMajors.every(category => category.majors.length === 0) && (
            <div className="p-3 text-center text-gray-500">
              No majors found
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

const days = ['S', 'M', 'T', 'W', 'R', 'F', 'A'];
const hours = [
  '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
];

const SimpleClassForm = ({ onAddClass }) => {
  const [classData, setClassData] = useState({
    title: '',
    professor: '',
    location: '',
    credits: '',
    schedule: Array(10).fill(Array(7).fill(false)),
    semester: '',
    year: new Date().getFullYear()
  });

  const [error, setError] = useState('');

  // Add helper function to get current semester info
  const getCurrentSemesterInfo = () => {
    const now = new Date();
    const month = now.getMonth(); // 0-11
    let semester;
    if (month >= 0 && month <= 4) {        // January - May
      semester = 'Spring';
    } else if (month >= 5 && month <= 7) { // June - August
      semester = 'Summer';
    } else {                               // September - December
      semester = 'Fall';
    }
    return { semester, year: now.getFullYear() };
  };

  // Set initial semester and year when component mounts
  useEffect(() => {
    const { semester, year } = getCurrentSemesterInfo();
    setClassData(prev => ({
      ...prev,
      semester,
      year
    }));
  }, []);

  const handleScheduleChange = (hourIndex, dayIndex) => {
    setClassData(prevState => ({
      ...prevState,
      schedule: prevState.schedule.map((hour, i) =>
        i === hourIndex 
          ? hour.map((day, j) => (j === dayIndex ? !day : day))
          : hour
      )
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!classData.title.trim()) {
      setError('Please enter a class name');
      return;
    }

    const scheduleString = convertScheduleToString(classData.schedule);
    onAddClass({
      ...classData,
      id: `class_${Date.now()}`,
      schedule: {
        displayString: scheduleString,
        raw: scheduleString
      }
    });
    
    // Reset form but keep semester and year
    const { semester, year } = classData;
    setClassData({
      title: '',
      professor: '',
      location: '',
      credits: '',
      schedule: Array(10).fill(Array(7).fill(false)),
      semester,
      year
    });
    setError('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Required Field */}
      <div>
        <input
          type="text"
          value={classData.title}
          onChange={(e) => {
            setClassData(prev => ({ ...prev, title: e.target.value }));
            if (error) setError('');
          }}
          placeholder="Class Name *"
          className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#F0EDE7] focus:border-transparent
            ${error ? 'border-red-300' : 'border-gray-200'}`}
        />
        {error && (
          <p className="mt-1 text-sm text-red-500">
            {error}
          </p>
        )}
      </div>

      {/* Optional Fields */}
      <div className="grid grid-cols-2 gap-4">
        <input
          type="text"
          value={classData.professor}
          onChange={(e) => setClassData(prev => ({ ...prev, professor: e.target.value }))}
          placeholder="Professor Name (optional)"
          className="p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#F0EDE7] focus:border-transparent"
        />
        <input
          type="text"
          value={classData.location}
          onChange={(e) => setClassData(prev => ({ ...prev, location: e.target.value }))}
          placeholder="Location (optional)"
          className="p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#F0EDE7] focus:border-transparent"
        />
      </div>

      {/* Semester and Credits Row */}
      <div className="grid grid-cols-3 gap-4">
        <select
          value={classData.semester}
          onChange={(e) => setClassData(prev => ({ ...prev, semester: e.target.value }))}
          className="p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#F0EDE7] focus:border-transparent"
        >
          <option value="">Select Semester</option>
          <option value="Fall">Fall</option>
          <option value="Spring">Spring</option>
          <option value="Summer">Summer</option>
        </select>

        <select
          value={classData.year}
          onChange={(e) => setClassData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
          className="p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#F0EDE7] focus:border-transparent"
        >
          {Array.from({ length: 5 }, (_, i) => {
            const year = new Date().getFullYear() + i;
            return (
              <option key={year} value={year}>
                {year}
              </option>
            );
          })}
        </select>

        <input
          type="number"
          value={classData.credits}
          onChange={(e) => setClassData(prev => ({ ...prev, credits: e.target.value }))}
          placeholder="Credits (optional)"
          min="0"
          step="0.5"
          className="p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#F0EDE7] focus:border-transparent"
        />
      </div>

      {/* Schedule Grid */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Class Schedule</h3>
              <p className="text-sm text-gray-500">Click time slots to set schedule</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#F0EDE7] border border-gray-200 rounded"></div>
                <span className="text-sm text-gray-500">Selected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-100 border border-gray-200 rounded"></div>
                <span className="text-sm text-gray-500">Available</span>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="p-3 text-left font-medium text-sm text-gray-500 w-24"></th>
                {days.map((day, index) => (
                  <th
                    key={index}
                    className="p-3 text-center font-medium text-sm text-gray-600 border-l border-gray-200"
                  >
                    <span className="block">{day}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {classData.schedule.map((hour, hourIndex) => (
                <tr key={hourIndex} className={`
                  hover:bg-gray-50
                  ${hourIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                `}>
                  <td className="p-3 text-sm font-medium text-gray-600 border-t border-gray-200">
                    {hours[hourIndex]}
                  </td>
                  {hour.map((isSelected, dayIndex) => (
                    <td
                      key={dayIndex}
                      onClick={() => handleScheduleChange(hourIndex, dayIndex)}
                      className="border-t border-l border-gray-200 cursor-pointer relative group h-12"
                    >
                      <div className={`
                        absolute inset-0 m-1 rounded transition-colors duration-150
                        ${isSelected ? 'bg-[#F0EDE7] hover:bg-[#E5E2DC]' : 'bg-gray-100 hover:bg-[#F0EDE7]/50'}
                      `}>
                        {isSelected && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-gray-600"></div>
                          </div>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-sm text-gray-500 flex items-center gap-2">
        <AlertCircle size={16} />
        Tip: Click multiple cells to select recurring class times
      </div>

      <button
        type="submit"
        className="w-full p-3 bg-[#F0EDE7] hover:bg-[#E5E2DC] text-gray-800 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        <Plus size={20} />
        Add Class
      </button>
    </form>
  );
};

const ClassList = ({ classes, onRemoveClass }) => {
  if (classes.length === 0) return null;

  return (
    <div className="mt-6 space-y-3">
      <div className="text-sm text-gray-500 mb-2">Added Classes ({classes.length})</div>
      {classes.map((classItem) => (
        <div
          key={classItem.id}
          className="bg-gray-50 rounded-lg p-4 relative group"
        >
          <button
            onClick={() => onRemoveClass(classItem.id)}
            className="absolute right-2 top-2 p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X size={16} />
          </button>
          <h3 className="font-medium text-gray-900">{classItem.title}</h3>
          
          <div className="mt-2 space-y-1">
            {classItem.professor && (
              <div className="text-sm text-gray-500">
                Professor: {classItem.professor}
              </div>
            )}
            
            {classItem.credits && (
              <div className="text-sm text-gray-500">
                Credits: {classItem.credits}
              </div>
            )}

            {classItem.semester && classItem.year && (
              <div className="text-sm text-gray-500">
                Semester: {classItem.semester} {classItem.year}
              </div>
            )}

            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
              {classItem.location && (
                <div className="flex items-center gap-1">
                  <MapPin size={14} />
                  {classItem.location}
                </div>
              )}
              {classItem.schedule?.displayString && (
                <div className="flex items-center gap-1">
                  <Clock size={14} />
                  {classItem.schedule.displayString}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const formatTime = (hour, isEnd = false) => {
  const hour12 = hour % 12 || 12;
  const period = hour < 12 ? 'AM' : 'PM';
  return `${hour12.toString().padStart(2, '0')}:${isEnd ? '50' : '00'} ${period}`;
};

const formatTimeRange = (startHour, endHour) => {
  const startTime = formatTime(startHour);
  const endTime = formatTime(endHour, true);
  return `${startTime}-${endTime}`;
};

const convertScheduleToString = (scheduleGrid) => {
    if (!Array.isArray(scheduleGrid)) {
        return '';
    }

    // First, collect all selected slots by day
    const daySlots = {};
    
    scheduleGrid.forEach((hourRow, hourIndex) => {
        if (!Array.isArray(hourRow)) return;

        hourRow.forEach((isSelected, dayIndex) => {
            if (isSelected) {
                const day = days[dayIndex];
                if (!daySlots[day]) {
                    daySlots[day] = [];
                }
                daySlots[day].push(hourIndex);
            }
        });
    });

    // Then, combine consecutive slots for each day
    const combinedSlots = [];
    
    Object.entries(daySlots).forEach(([day, hours]) => {
        hours.sort((a, b) => a - b);
        
        let startHour = hours[0];
        let prevHour = startHour;
        
        for (let i = 1; i <= hours.length; i++) {
            const currentHour = hours[i];
            
            // If we're at the end or there's a gap in consecutive hours
            if (i === hours.length || currentHour !== prevHour + 1) {
                // Find existing slot with the same time range
                const timeRange = formatTimeRange(startHour + 8, prevHour + 8);
                let slot = combinedSlots.find(s => s.timeRange === timeRange);
                
                if (slot) {
                    slot.days += day;
                } else {
                    combinedSlots.push({
                        days: day,
                        timeRange: timeRange
                    });
                }
                
                startHour = currentHour;
            }
            prevHour = currentHour;
        }
    });

    // Sort slots by start time
    combinedSlots.sort((a, b) => {
        const timeA = a.timeRange.split('-')[0];
        const timeB = b.timeRange.split('-')[0];
        return timeA.localeCompare(timeB);
    });
    
    // Generate the final schedule string
    const scheduleString = combinedSlots
        .map(slot => `${slot.days} ${slot.timeRange}`)
        .join(', ');

    return scheduleString;
};

const Onboarding = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  
  const [formData, setFormData] = useState({
    school: '',
    major: '',
    semester: calculateCurrentSemester(),
  });
  const [classes, setClasses] = useState([]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddClass = (newClass) => {
    setClasses(prev => [...prev, newClass]);
  };

  const handleRemoveClass = (classId) => {
    setClasses(prev => prev.filter(c => c.id !== classId));
  };

  const handleNext = async () => {
    if (step === totalSteps) {
      try {
        const result = await saveUserProfile(user.id, {
          ...formData,
          classes: classes
        });
        
        if (result.success) {
          navigate('/');  // or wherever you want to redirect after success
        } else {
          toast.error('Failed to save profile. Please try again.');
        }
      } catch (error) {
        console.error('Failed to save profile:', error);
        toast.error('Failed to save profile. Please try again.');
      }
    } else {
      setStep(step + 1);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.school;
      case 2:
        return formData.major && formData.semester;
      case 3:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-16 pb-24">
      <StepIndicator currentStep={step} totalSteps={totalSteps} />

      <AnimatePresence mode="wait">
        {step === 1 && (
          <OnboardingStep
            icon={School}
            title="Welcome to Classify!"
            subtitle="Let's start with your school"
            stepNumber={1}
            totalSteps={totalSteps}
          >
            <div className="space-y-6">
              <Dropdown
                options={COLLEGES}
                value={formData.school}
                onChange={(value) => handleInputChange('school', value)}
                placeholder="Search for your school..."
                fetchOptions={fetchColleges}
              />
            </div>
          </OnboardingStep>
        )}

        {step === 2 && (
          <OnboardingStep
            icon={GraduationCap}
            title="Your Academic Focus"
            subtitle="Tell us about your major and current semester"
            stepNumber={2}
            totalSteps={totalSteps}
          >
            <div className="space-y-6">
              <MajorDropdown
                value={formData.major}
                onChange={(value) => handleInputChange('major', value)}
                placeholder="Search for your major..."
              />
              <Dropdown
                options={SEMESTERS}
                value={formData.semester}
                onChange={(value) => handleInputChange('semester', value)}
                placeholder="Select current semester..."
              />
            </div>
          </OnboardingStep>
        )}

        {step === 3 && (
          <OnboardingStep
            icon={BookOpen}
            title="Add Your Classes"
            subtitle="Add the classes you're taking this semester"
            stepNumber={3}
            totalSteps={totalSteps}
          >
            <div>
              <SimpleClassForm onAddClass={handleAddClass} />
              <ClassList 
                classes={classes} 
                onRemoveClass={handleRemoveClass} 
              />
              {classes.length > 0 && (
                <div className="mt-4 text-sm text-gray-500 text-center">
                  You can always add more classes later from your dashboard
                </div>
              )}
            </div>
          </OnboardingStep>
        )}
      </AnimatePresence>

      <NavigationButtons
        onBack={step > 1 ? () => setStep(step - 1) : null}
        onNext={handleNext}
        isLastStep={step === totalSteps}
        isValid={isStepValid()}
      />
    </div>
  );
};

export default Onboarding; 